const { renderProjectiveRaster } = require('./projectiveRaster');

const DEFAULT_CORNERS = [
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
];

const MAX_COORDINATE = 2;
const MIN_COORDINATE = -1;
const GEOMETRY_EPSILON = 1e-8;
const MAX_PROJECTED_COORDINATE = 1e6;
const MAX_SAFE_DENOMINATOR = 0.01;

function defaultCorners() {
  return DEFAULT_CORNERS.map(point => ({ ...point }));
}

function cross(a, b, c) {
  return (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
}

function polygonArea(corners) {
  let area = 0;
  for (let i = 0; i < corners.length; i++) {
    const current = corners[i];
    const next = corners[(i + 1) % corners.length];
    area += current.x * next.y - next.x * current.y;
  }
  return Math.abs(area) / 2;
}

function validCorners(corners) {
  if (!Array.isArray(corners) || corners.length !== 4) return false;
  if (corners.some(point => !point
    || !Number.isFinite(point.x) || !Number.isFinite(point.y)
    || point.x < MIN_COORDINATE || point.x > MAX_COORDINATE
    || point.y < MIN_COORDINATE || point.y > MAX_COORDINATE)) return false;

  // A perspective quad must be convex, with no collapsed sides or corners.
  const turns = corners.map((point, index) => cross(
    point, corners[(index + 1) % 4], corners[(index + 2) % 4]
  ));
  if (turns.some(turn => Math.abs(turn) <= GEOMETRY_EPSILON)) return false;
  if (!turns.every(turn => turn > 0) && !turns.every(turn => turn < 0)) return false;
  return polygonArea(corners) > GEOMETRY_EPSILON;
}

// Solve the 8 unknown coefficients of a unit-square-to-quad homography.
function getHomography(corners) {
  const targets = corners.map(point => ({ x: point.x, y: point.y }));
  const source = [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: 0, y: 1 },
  ];
  const matrix = [];

  for (let i = 0; i < 4; i++) {
    const { x: u, y: v } = source[i];
    const { x, y } = targets[i];
    matrix.push([u, v, 1, 0, 0, 0, -x * u, -x * v, x]);
    matrix.push([0, 0, 0, u, v, 1, -y * u, -y * v, y]);
  }

  for (let column = 0; column < 8; column++) {
    let pivot = column;
    for (let row = column + 1; row < 8; row++) {
      if (Math.abs(matrix[row][column]) > Math.abs(matrix[pivot][column])) pivot = row;
    }
    if (Math.abs(matrix[pivot][column]) <= GEOMETRY_EPSILON) return null;
    [matrix[column], matrix[pivot]] = [matrix[pivot], matrix[column]];

    const divisor = matrix[column][column];
    for (let j = column; j <= 8; j++) matrix[column][j] /= divisor;
    for (let row = 0; row < 8; row++) {
      if (row === column) continue;
      const factor = matrix[row][column];
      for (let j = column; j <= 8; j++) matrix[row][j] -= factor * matrix[column][j];
    }
  }

  return matrix.map(row => row[8]);
}

function mapPoint(corners, u, v) {
  if (!validCorners(corners) || !Number.isFinite(u) || !Number.isFinite(v)) return null;
  const h = getHomography(corners);
  if (!h) return null;
  return projectWithHomography(h, u, v);
}

function projectWithHomography(h, u, v) {
  const denominator = h[6] * u + h[7] * v + 1;
  if (Math.abs(denominator) <= GEOMETRY_EPSILON) return null;
  const x = (h[0] * u + h[1] * v + h[2]) / denominator;
  const y = (h[3] * u + h[4] * v + h[5]) / denominator;
  return Number.isFinite(x) && Number.isFinite(y)
    && Math.abs(x) <= MAX_PROJECTED_COORDINATE
    && Math.abs(y) <= MAX_PROJECTED_COORDINATE ? { x, y } : null;
}

function affineFromTriangles(source, target) {
  const [p0, p1, p2] = source;
  const [q0, q1, q2] = target;
  const determinant = p0.x * (p1.y - p2.y)
    + p1.x * (p2.y - p0.y)
    + p2.x * (p0.y - p1.y);
  if (Math.abs(determinant) <= GEOMETRY_EPSILON) return null;

  const a = (q0.x * (p1.y - p2.y)
    + q1.x * (p2.y - p0.y) + q2.x * (p0.y - p1.y)) / determinant;
  const c = (q0.x * (p2.x - p1.x)
    + q1.x * (p0.x - p2.x) + q2.x * (p1.x - p0.x)) / determinant;
  const e = (q0.x * (p1.x * p2.y - p2.x * p1.y)
    + q1.x * (p2.x * p0.y - p0.x * p2.y)
    + q2.x * (p0.x * p1.y - p1.x * p0.y)) / determinant;
  const b = (q0.y * (p1.y - p2.y)
    + q1.y * (p2.y - p0.y) + q2.y * (p0.y - p1.y)) / determinant;
  const d = (q0.y * (p2.x - p1.x)
    + q1.y * (p0.x - p2.x) + q2.y * (p1.x - p0.x)) / determinant;
  const f = (q0.y * (p1.x * p2.y - p2.x * p1.y)
    + q1.y * (p2.x * p0.y - p0.x * p2.y)
    + q2.y * (p0.x * p1.y - p1.x * p0.y)) / determinant;
  return [a, b, c, d, e, f];
}

function imageDimensions(image) {
  return {
    width: image?.naturalWidth || image?.videoWidth || image?.width || 0,
    height: image?.naturalHeight || image?.videoHeight || image?.height || 0,
  };
}

function toOutputPoint(point, width, height) {
  if (!point) return null;
  const x = point.x * width;
  const y = point.y * height;
  if (!Number.isFinite(x) || !Number.isFinite(y)
    || Math.abs(x) > MAX_PROJECTED_COORDINATE || Math.abs(y) > MAX_PROJECTED_COORDINATE) return null;
  return { x, y };
}

function sourceRectBounds(rect) {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ];
}

function normalizedBounds(bounds, rect) {
  return bounds.map(point => ({
    u: (point.x - rect.x) / rect.width,
    v: (point.y - rect.y) / rect.height,
  }));
}

function expandedSourceRect(rect, sourceWidth, sourceHeight, factor) {
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;
  const left = rect.x * (1 - factor);
  const top = rect.y * (1 - factor);
  const expandedRight = right + (sourceWidth - right) * factor;
  const expandedBottom = bottom + (sourceHeight - bottom) * factor;
  return {
    x: left,
    y: top,
    width: expandedRight - left,
    height: expandedBottom - top,
  };
}

function safeProjectedSourceRegion(homography, sourceWidth, sourceHeight, sourceRect, outputWidth, outputHeight) {
  if (![sourceWidth, sourceHeight, outputWidth, outputHeight].every(Number.isFinite)
    || sourceWidth <= 0 || sourceHeight <= 0 || outputWidth <= 0 || outputHeight <= 0
    || !sourceRect || ![sourceRect.x, sourceRect.y, sourceRect.width, sourceRect.height].every(Number.isFinite)
    || sourceRect.width <= 0 || sourceRect.height <= 0 || sourceRect.x < 0 || sourceRect.y < 0
    || sourceRect.x + sourceRect.width > sourceWidth
    || sourceRect.y + sourceRect.height > sourceHeight) return null;

  const logicalBounds = normalizedBounds(sourceRectBounds(sourceRect), sourceRect);
  const logicalDenominators = logicalBounds.map(point =>
    homography[6] * point.u + homography[7] * point.v + 1
  );
  if (logicalDenominators.some(value => !Number.isFinite(value) || value <= GEOMETRY_EPSILON)
    || !logicalDenominators.every(value => value > 0)) return null;
  const denominatorMargin = Math.min(MAX_SAFE_DENOMINATOR, Math.min(...logicalDenominators) / 2);

  const projectRegion = factor => {
    const rect = expandedSourceRect(sourceRect, sourceWidth, sourceHeight, factor);
    const bounds = sourceRectBounds(rect);
    const normalized = normalizedBounds(bounds, sourceRect);
    const denominators = normalized.map(point =>
      homography[6] * point.u + homography[7] * point.v + 1
    );
    if (denominators.some(value => !Number.isFinite(value) || value < denominatorMargin)) return null;

    const projected = normalized.map(point =>
      toOutputPoint(projectWithHomography(homography, point.u, point.v), outputWidth, outputHeight)
    );
    if (projected.some(point => !point)) return null;
    return { rect, corners: projected };
  };

  // The logical rectangle corresponds to the original, finite unit-square
  // mapping. Keep it as the guaranteed minimum visible crop, then find the
  // largest uniform expansion toward the bitmap bounds that stays away from
  // the homography horizon.
  let best = projectRegion(0);
  if (!best) return null;
  const full = projectRegion(1);
  if (full) return full;

  let low = 0;
  let high = 1;
  for (let i = 0; i < 40; i++) {
    const middle = (low + high) / 2;
    const candidate = projectRegion(middle);
    if (candidate) {
      low = middle;
      best = candidate;
    } else {
      high = middle;
    }
  }
  return best;
}

function drawTriangle(ctx, image, source, target, sourceWidth, sourceHeight) {
  const transform = affineFromTriangles(source, target);
  if (!transform) return;

  // Include texture neighbours for interpolation. A second source-space clip
  // used to cut off those pixels and introduce a rectangular grid of seams.
  const minX = Math.max(0, Math.min(...source.map(point => point.x)) - 1);
  const minY = Math.max(0, Math.min(...source.map(point => point.y)) - 1);
  const maxX = Math.min(sourceWidth, Math.max(...source.map(point => point.x)) + 1);
  const maxY = Math.min(sourceHeight, Math.max(...source.map(point => point.y)) + 1);
  const cropWidth = maxX - minX;
  const cropHeight = maxY - minY;
  if (cropWidth <= 0 || cropHeight <= 0) return;

  ctx.save();
  try {
    ctx.beginPath();
    ctx.moveTo(target[0].x, target[0].y);
    ctx.lineTo(target[1].x, target[1].y);
    ctx.lineTo(target[2].x, target[2].y);
    ctx.closePath();
    ctx.clip();
    ctx.transform(...transform);
    ctx.drawImage(image, minX, minY, cropWidth, cropHeight, minX, minY, cropWidth, cropHeight);
  } finally {
    ctx.restore();
  }
}

function projectedSourceCorners(corners, sourceWidth, sourceHeight, sourceRect, outputWidth, outputHeight) {
  if (!validCorners(corners)) return null;
  const homography = getHomography(corners);
  if (!homography) return null;
  const rect = sourceRect || { x: 0, y: 0, width: sourceWidth, height: sourceHeight };
  const projected = safeProjectedSourceRegion(
    homography, sourceWidth, sourceHeight, rect, outputWidth, outputHeight
  );
  return projected ? projected.corners : null;
}

// Raster effects need enough source pixels for their largest local stretch,
// including narrow-to-wide projective warps, not only the quad's bounding box.
function perspectiveRasterQuality(width, height, corners, density = 1) {
  if (!(width > 0) || !(height > 0)) return 1;
  const h = getHomography(validCorners(corners) ? corners : defaultCorners());
  let stretch = 1;
  if (h) {
    for (const u of [0, 0.5, 1]) for (const v of [0, 0.5, 1]) {
      const d = h[6] * u + h[7] * v + 1;
      if (Math.abs(d) <= GEOMETRY_EPSILON) continue;
      const x = h[0] * u + h[1] * v + h[2];
      const y = h[3] * u + h[4] * v + h[5];
      const a = (h[0] * d - x * h[6]) / (d * d);
      const b = (h[1] * d - x * h[7]) / (d * d) * width / height;
      const c = (h[3] * d - y * h[6]) / (d * d) * height / width;
      const e = (h[4] * d - y * h[7]) / (d * d);
      const trace = a * a + b * b + c * c + e * e;
      const determinant = a * e - b * c;
      const norm = Math.sqrt((trace + Math.sqrt(Math.max(0, trace * trace - 4 * determinant * determinant))) / 2);
      if (Number.isFinite(norm)) stretch = Math.max(stretch, norm);
    }
  }
  const base = Number.isFinite(density) ? Math.max(1, density) : 1;
  return Math.ceil(base * stretch - 1e-9);
}

function boundedRasterRatio(width, height, quality) {
  // Permit ratios below one for exceptionally large sources: flooring this at
  // one would defeat the memory bound before Konva allocates the canvas.
  return Math.min(Math.max(1, quality), Math.sqrt(8000000 / Math.max(1, width * height)));
}

function drawPerspective(ctx, image, width, height, corners, sourceRect, revision) {
  if (!ctx || !image || !Number.isFinite(width) || !Number.isFinite(height)
    || width <= 0 || height <= 0 || !validCorners(corners)) return false;
  const homography = getHomography(corners);
  if (!homography) return false;
  const { width: sourceWidth, height: sourceHeight } = imageDimensions(image);
  if (sourceWidth <= 0 || sourceHeight <= 0) return false;
  const logicalRect = sourceRect || {
    x: 0, y: 0, width: sourceWidth, height: sourceHeight,
  };
  const projection = safeProjectedSourceRegion(
    homography, sourceWidth, sourceHeight, logicalRect, width, height
  );
  if (!projection) return false;
  const sourceRegion = projection.rect;
  if (typeof document !== 'undefined' && document.createElement) {
    const raster = renderProjectiveRaster(ctx, image, sourceWidth, sourceHeight, homography,
      logicalRect, sourceRegion, projection.corners, width, height, revision);
    ctx.save();
    try {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(raster.canvas, raster.x, raster.y, raster.width, raster.height);
    } finally {
      ctx.restore();
    }
    return true;
  }

  const segments = 12;
  const normalizeSourcePoint = (x, y) => ({
    u: (x - logicalRect.x) / logicalRect.width,
    v: (y - logicalRect.y) / logicalRect.height,
  });

  const grid = Array.from({ length: segments + 1 }, (_, y) =>
    Array.from({ length: segments + 1 }, (_, x) => {
      const sourceX = sourceRegion.x + x / segments * sourceRegion.width;
      const sourceY = sourceRegion.y + y / segments * sourceRegion.height;
      const { u, v } = normalizeSourcePoint(sourceX, sourceY);
      const point = projectWithHomography(homography, u, v);
      return {
        source: { x: sourceX, y: sourceY },
        target: toOutputPoint(point, width, height),
      };
    })
  );
  if (grid.some(row => row.some(point => !point.target))) return false;

  // The helper returns precisely the finite source region used by this mesh,
  // so hit testing can share the same visible quadrilateral.
  const outer = projection.corners;
  ctx.save();
  try {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.beginPath();
    ctx.moveTo(outer[0].x, outer[0].y);
    ctx.lineTo(outer[1].x, outer[1].y);
    ctx.lineTo(outer[2].x, outer[2].y);
    ctx.lineTo(outer[3].x, outer[3].y);
    ctx.closePath();
    ctx.clip();

    for (let y = 0; y < segments; y++) {
      for (let x = 0; x < segments; x++) {
        const topLeft = grid[y][x];
        const topRight = grid[y][x + 1];
        const bottomRight = grid[y + 1][x + 1];
        const bottomLeft = grid[y + 1][x];

        drawTriangle(ctx, image,
          [topLeft.source, topRight.source, bottomRight.source],
          [topLeft.target, topRight.target, bottomRight.target], sourceWidth, sourceHeight);
        drawTriangle(ctx, image,
          [topLeft.source, bottomRight.source, bottomLeft.source],
          [topLeft.target, bottomRight.target, bottomLeft.target], sourceWidth, sourceHeight);
      }
    }
  } finally {
    ctx.restore();
  }
  return true;
}

module.exports = {
  perspectiveRasterQuality,
  boundedRasterRatio,
  defaultCorners,
  validCorners,
  getHomography,
  mapPoint,
  drawPerspective,
  projectedSourceCorners,
};